import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type DependencyList,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

import { getStableId } from '@/statsig';

import type { Ampli } from './generated';

const CONSENT_STORAGE_KEY = 'cconsent';

/**
 * - `pending`: server render / before hydration; consent is unknown.
 * - `undecided`: the visitor hasn't accepted or declined yet.
 * - `declined`: the visitor declined; Amplitude stays unloaded.
 * - `optedIn`: Amplitude is loading (or loaded) for this visitor.
 */
type ConsentState =
  | { status: 'pending' | 'undecided' | 'declined'; ampli?: never }
  | { status: 'optedIn'; ampli: Promise<Ampli | undefined> };

const Context = createContext<
  [ConsentState, Dispatch<SetStateAction<ConsentState>>]
>([{ status: 'pending' }, () => {}]);
const DisabledContext = createContext(false);

let cachedAmpli: Promise<Ampli | undefined> | undefined;

const loadAmpli = async (): Promise<Ampli | undefined> => {
  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
  if (!apiKey) {
    return undefined;
  }

  const [{ ampli }, stableId] = await Promise.all([
    import('./generated'),
    getStableId(),
  ]);
  ampli.load({
    client: {
      apiKey,
      configuration: {
        autocapture: { elementInteractions: true },
        deviceId: stableId,
        serverUrl: 'https://amplitude.getbudgy.co.uk/2/httpapi',
      },
    },
  });

  const [{ sessionReplayPlugin }, engagementModule] = await Promise.all([
    import('@amplitude/plugin-session-replay-browser'),
    import('@amplitude/engagement-browser'),
  ]);
  ampli.client.add(
    sessionReplayPlugin({
      sampleRate: 1,
      configServerUrl: 'https://config.amplitude.getbudgy.co.uk/config',
      trackServerUrl:
        'https://session-replay.amplitude.getbudgy.co.uk/sessions/v2/track',
    }),
  );
  ampli.client.add(
    engagementModule.plugin({
      serverZone: 'EU',
      serverUrl: 'https://guides.amplitude.getbudgy.co.uk',
    }),
  );
  return ampli;
};

const load = () => {
  cachedAmpli ??= loadAmpli().catch(() => undefined);
  return cachedAmpli;
};

// Client-only: called from an effect so SSR always renders `pending`.
const readConsentState = (): ConsentState => {
  switch (window.localStorage.getItem(CONSENT_STORAGE_KEY)) {
    case 'true':
      return { status: 'optedIn', ampli: load() };
    case 'false':
      return { status: 'declined' };
    default:
      return { status: 'undecided' };
  }
};

export const AmpliProvider = ({ children }: { children: ReactNode }) => {
  const value = useState<ConsentState>({ status: 'pending' });
  const setState = value[1];

  useEffect(() => {
    setState(readConsentState());
  }, [setState]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const AmpliDisabledProvider = ({
  children,
  isDisabled,
}: {
  children: ReactNode;
  isDisabled: boolean;
}) => {
  const [state] = useContext(Context);

  useEffect(() => {
    if (state.status !== 'optedIn') {
      return;
    }

    void state.ampli.then((ampli) => ampli?.client.setOptOut(isDisabled));

    return () => {
      void state.ampli.then((ampli) => ampli?.client.setOptOut(false));
    };
  }, [isDisabled, state]);

  return (
    <DisabledContext.Provider value={isDisabled}>
      {children}
    </DisabledContext.Provider>
  );
};

/** Runs `cb` once Amplitude has loaded; a no-op unless the visitor opted in. */
export const useAmpli = (): ((cb: (ampli: Ampli) => void) => void) => {
  const [state] = useContext(Context);
  const isDisabled = useContext(DisabledContext);

  return useCallback(
    (cb: (ampli: Ampli) => void) => {
      if (isDisabled || state.status !== 'optedIn') {
        return;
      }

      void state.ampli.then((ampli) => {
        if (ampli) {
          cb(ampli);
        }
      });
    },
    [isDisabled, state],
  );
};

/**
 * Runs `fn` with Amplitude after render whenever `deps` change, and again once
 * the visitor opts in, so mount-time view events aren't lost to consent
 * loading. Like `useEffect`, deps are compared with `Object.is`.
 */
export const useAmpliEffect = (
  fn: (ampli: Ampli) => void,
  deps: DependencyList,
) => {
  const ampli = useAmpli();
  const lastRunRef = useRef<{
    ampli: typeof ampli;
    deps: DependencyList;
  } | null>(null);

  useEffect(() => {
    const lastRun = lastRunRef.current;
    if (
      lastRun?.ampli === ampli &&
      areDependencyListsEqual(lastRun.deps, deps)
    ) {
      return;
    }

    lastRunRef.current = { ampli, deps };
    ampli(fn);
  });
};

/**
 * Runs `fn` with Amplitude at most once per mount, as soon as the visitor has
 * opted in. Use for view events that must not repeat on re-render or consent changes.
 */
export const useAmpliEffectOnce = (fn: (ampli: Ampli) => void) => {
  const ampli = useAmpli();
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) {
      return;
    }

    ampli((client) => {
      if (hasRunRef.current) {
        return;
      }

      hasRunRef.current = true;
      fn(client);
    });
  });
};

const areDependencyListsEqual = (
  previous: DependencyList,
  next: DependencyList,
) =>
  previous.length === next.length &&
  previous.every((value, index) => Object.is(value, next[index]));

export const useConsentStatus = () => useContext(Context)[0].status;

export const useConsentActions = () => {
  const [, setState] = useContext(Context);

  const handleAccept = useCallback(() => {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, 'true');
    setState({ status: 'optedIn', ampli: load() });
  }, [setState]);

  const handleDecline = useCallback(() => {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, 'false');
    setState({ status: 'declined' });
  }, [setState]);

  return { handleAccept, handleDecline };
};
